<template>
    <div class="page order-cancellations-page">
        <div class="page-header">
            <h1>取消申請審核</h1>
            <p>審核取消與退款申請</p>
        </div>

        <div class="content vaporwave-theme">
            <!-- 搜尋與篩選區域 -->
            <div class="search-filter-section">
                <div class="toolbar">
                    <div class="search-box filters-wrap">
                        <div class="search-row">
                            <!-- 訂單編號搜尋 -->
                            <a-input
                                v-model:value="filters.order_no"
                                placeholder="訂單編號"
                                size="middle"
                                class="w-60 order-no-input"
                                allow-clear
                                @press-enter="handleSearch"
                            />

                            <!-- 申請狀態 -->
                            <a-select
                                v-model:value="filters.status"
                                :options="statusOptions"
                                placeholder="申請狀態"
                                size="middle"
                                class="status-select w-40"
                                allow-clear
                            />

                            <!-- 退款狀態 -->
                            <a-select
                                v-model:value="filters.refund_status"
                                :options="refundStatusOptions"
                                placeholder="退款狀態"
                                size="middle"
                                class="status-select w-40"
                                allow-clear
                            />

                            <!-- 日期範圍 -->
                            <a-range-picker
                                v-model:value="dateRange"
                                :placeholder="['開始日期', '結束日期']"
                                size="middle"
                                class="status-select"
                                @change="handleDateRangeChange"
                            />
                        </div>
                    </div>
                    <a-space class="toolbar-actions">
                        <a-button
                            v-if="
                                selectedRowKeys.length > 0 && canApproveRequest
                            "
                            size="middle"
                            class="batch-approve-btn"
                            :loading="batchLoading"
                            @click="handleBatchApprove"
                        >
                            批量核准 ({{ selectedRowKeys.length }})
                        </a-button>
                        <a-button
                            v-if="
                                selectedRowKeys.length > 0 && canRejectRequest
                            "
                            size="middle"
                            class="batch-reject-btn"
                            :loading="batchLoading"
                            @click="handleBatchRejectOpen"
                        >
                            批量拒絕 ({{ selectedRowKeys.length }})
                        </a-button>
                        <a-button
                            size="middle"
                            class="unified-search-btn"
                            @click="handleSearch"
                        >
                            查詢
                        </a-button>
                        <a-button
                            size="middle"
                            class="unified-reset-btn"
                            @click="handleReset"
                        >
                            重置
                        </a-button>
                    </a-space>
                </div>
            </div>

            <!-- 取消申請列表表格 -->
            <DataTable
                title=""
                :columns="columns"
                :data-source="requests"
                :pagination="pagination"
                :loading="loading"
                :no-card="true"
                :row-selection="rowSelection"
                @refresh="loadRequests"
                @change="handleTableChange"
            >
                <template #bodyCell="{ column, record }">
                    <!-- 訂單編號（可點擊開啟詳情） -->
                    <template v-if="column.key === 'order_no'">
                        <a
                            v-if="canViewRequest"
                            class="order-no-link"
                            @click="handleViewDetail(record)"
                        >
                            {{ record.order_no }}
                        </a>
                        <span v-else class="order-no-text">
                            {{ record.order_no }}
                        </span>
                    </template>

                    <!-- 會員名稱 -->
                    <template v-if="column.key === 'user_name'">
                        <a
                            v-if="record.user_id && canViewMember"
                            class="user-link"
                            @click.stop="handleViewMember(record.user_id)"
                        >
                            {{ record.user_name || '-' }}
                        </a>
                        <span v-else class="user-name">
                            {{ record.user_name || '-' }}
                        </span>
                    </template>

                    <!-- 申請狀態 -->
                    <template v-if="column.key === 'status'">
                        <a-tag
                            :color="getStatusColor(record.status)"
                            class="status-tag"
                        >
                            {{
                                record.status_label ||
                                getStatusText(record.status)
                            }}
                        </a-tag>
                    </template>

                    <!-- 退款狀態 -->
                    <template v-if="column.key === 'refund_status'">
                        <a-tag
                            v-if="record.refund_required"
                            :color="getRefundStatusColor(record.refund_status)"
                            class="refund-status-tag"
                        >
                            {{
                                record.refund_status_label ||
                                getRefundStatusText(record.refund_status)
                            }}
                        </a-tag>
                        <span v-else class="no-refund">無需退款</span>
                    </template>

                    <!-- 退款範圍 -->
                    <template v-if="column.key === 'refund_scope'">
                        <div class="refund-scope-cell">
                            <a-tag
                                :color="
                                    isPartialCancellationRequest(record)
                                        ? 'orange'
                                        : 'default'
                                "
                                class="refund-scope-tag"
                            >
                                {{
                                    getCancellationScopeLabel(record)
                                }}
                            </a-tag>
                            <div
                                class="refund-scope-summary"
                                :title="getCancellationItemsSummary(record)"
                            >
                                {{ getCancellationItemsSummary(record) }}
                            </div>
                        </div>
                    </template>

                    <!-- 申請退款金額 -->
                    <template v-if="column.key === 'requested_amount'">
                        <div class="requested-amount-cell">
                            <span class="requested-amount-value">
                                {{ formatAmount(record.requested_amount) }}
                            </span>
                            <span
                                v-if="isPartialCancellationRequest(record)"
                                class="requested-amount-order-total"
                            >
                                訂單
                                {{ formatAmount(record.order_total) }}
                            </span>
                        </div>
                    </template>

                    <!-- 訂單金額（保留欄位設置用） -->
                    <template v-if="column.key === 'order_total'">
                        {{ formatAmount(record.order_total) }}
                    </template>

                    <!-- 使用點數 -->
                    <template v-if="column.key === 'points'">
                        <div
                            v-if="
                                record.point_discounts &&
                                record.point_discounts.length > 0
                            "
                            class="points-display"
                        >
                            <a-tag
                                v-for="(
                                    discount, idx
                                ) in record.point_discounts"
                                :key="discount.id || idx"
                                class="point-tag"
                                :color="
                                    getPointTagColor(discount.point_type_code)
                                "
                            >
                                <span class="point-code">
                                    {{ discount.point_type_code }}
                                </span>
                                <span class="point-separator">:</span>
                                <span class="point-amount">
                                    {{ formatAmount(discount.amount) }}
                                </span>
                            </a-tag>
                        </div>
                        <span v-else class="no-points">-</span>
                    </template>

                    <!-- 申請時間 -->
                    <template v-if="column.key === 'created_at'">
                        {{ formatDateTime(record.created_at) }}
                    </template>

                    <!-- 審核時間 -->
                    <template v-if="column.key === 'reviewed_at'">
                        {{
                            record.reviewed_at
                                ? formatDateTime(record.reviewed_at)
                                : '-'
                        }}
                    </template>

                    <!-- 操作 -->
                    <template v-if="column.key === 'actions'">
                        <a-space class="action-buttons">
                            <!-- 待審核：可以核准或拒絕 -->
                            <template v-if="record.status === 'pending'">
                                <a-button
                                    v-if="canApproveRequest"
                                    size="small"
                                    class="action-btn-approve"
                                    @click="handleApprove(record)"
                                >
                                    核准
                                </a-button>
                                <a-button
                                    v-if="canRejectRequest"
                                    size="small"
                                    class="action-btn-reject"
                                    @click="handleReject(record)"
                                >
                                    拒絕
                                </a-button>
                            </template>

                            <!-- 已核准且需要退款：可以重試退款（排除人工退款的 ATM / 轉帳） -->
                            <template
                                v-if="
                                    record.status === 'approved' &&
                                    record.refund_required &&
                                    [
                                        'failed',
                                        'processing',
                                        'pending'
                                    ].includes(record.refund_status) &&
                                    ![
                                        'atm',
                                        'vacc',
                                        'webatm',
                                        'bank_transfer'
                                    ].includes((record.payment_method || '').toLowerCase())
                                "
                            >
                                <a-button
                                    v-if="canRetryRefund"
                                    size="small"
                                    class="action-btn-warning"
                                    @click="handleRetryRefund(record)"
                                >
                                    重試退款
                                </a-button>
                            </template>
                        </a-space>
                    </template>
                </template>
            </DataTable>
        </div>

        <!-- 申請詳情 Modal -->
        <DetailModal
            v-model:open="detailModalVisible"
            title="取消申請詳情"
            :width="900"
            :data="selectedRequest"
            :basic-info="requestBasicInfo"
            :column-count="2"
            :additional-content="true"
        >
            <template #additional>
                <div class="request-detail-content">
                    <!-- 申請資訊與退款明細 -->
                    <div class="detail-section">
                        <h3 class="section-title">申請資訊與明細</h3>
                        <a-descriptions :column="2" bordered class="mb-4">
                            <a-descriptions-item label="取消原因" :span="2">
                                <div class="reason-text">
                                    {{ selectedRequest?.reason || '-' }}
                                </div>
                            </a-descriptions-item>
                            <a-descriptions-item label="申請退款預估總額">
                                <span class="text-amber-700 font-bold">
                                    NT$ {{ (selectedRequest?.requested_amount || selectedRequest?.order_total || 0).toLocaleString() }}
                                </span>
                            </a-descriptions-item>
                            <a-descriptions-item label="行政手續費">
                                <span class="font-semibold text-gray-800">
                                    NT$ {{ (selectedRequest?.fee_amount || 0).toLocaleString() }}
                                </span>
                                <span v-if="selectedRequest?.fee_note" class="text-xs text-gray-500 ml-1">
                                    ({{ selectedRequest.fee_note }})
                                </span>
                            </a-descriptions-item>
                            <a-descriptions-item :label="getActualRefundLabel(selectedRequest)" :span="2">
                                <span class="text-cyan-700 font-black text-lg">
                                    NT$ {{ (selectedRequest?.actual_refund_amount || 0).toLocaleString() }}
                                </span>
                            </a-descriptions-item>
                        </a-descriptions>

                        <div v-if="selectedRequest?.items_payload && selectedRequest.items_payload.length > 0" class="mt-3">
                            <h4 class="info-title">學員申請退款商品與數量明細</h4>
                            <a-table
                                :data-source="getFormattedRequestItems(selectedRequest)"
                                :columns="requestItemColumns"
                                :pagination="false"
                                size="small"
                                bordered
                                row-key="order_item_id"
                            />
                        </div>
                    </div>

                    <!-- 退款資訊 -->
                    <div
                        v-if="selectedRequest?.refund_required"
                        class="detail-section"
                    >
                        <h3 class="section-title">退款資訊</h3>
                        <a-descriptions :column="2" bordered>
                            <a-descriptions-item label="退款狀態">
                                <a-tag
                                    :color="
                                        getRefundStatusColor(
                                            selectedRequest?.refund_status
                                        )
                                    "
                                >
                                    {{
                                        selectedRequest?.refund_status_label ||
                                        getRefundStatusText(
                                            selectedRequest?.refund_status
                                        )
                                    }}
                                </a-tag>
                            </a-descriptions-item>
                            <a-descriptions-item
                                v-if="selectedRequest?.refund_error_message"
                                label="退款錯誤訊息"
                            >
                                <span class="error-message">
                                    {{ selectedRequest.refund_error_message }}
                                </span>
                            </a-descriptions-item>
                        </a-descriptions>
                    </div>

                    <!-- 審核資訊 -->
                    <div
                        v-if="
                            selectedRequest?.status !== 'pending' &&
                            (selectedRequest?.admin_notes ||
                                selectedRequest?.reviewer_name ||
                                selectedRequest?.reviewed_at)
                        "
                        class="detail-section"
                    >
                        <h3 class="section-title">審核資訊</h3>
                        <a-descriptions :column="2" bordered>
                            <a-descriptions-item
                                v-if="selectedRequest?.reviewer_name"
                                label="審核人"
                            >
                                {{ selectedRequest.reviewer_name }}
                            </a-descriptions-item>
                            <a-descriptions-item
                                v-if="selectedRequest?.reviewed_at"
                                label="審核時間"
                            >
                                {{
                                    formatDateTime(selectedRequest.reviewed_at)
                                }}
                            </a-descriptions-item>
                            <a-descriptions-item
                                v-if="selectedRequest?.admin_notes"
                                label="審核備註"
                                :span="2"
                            >
                                <div class="notes-text">
                                    {{ selectedRequest.admin_notes }}
                                </div>
                            </a-descriptions-item>
                        </a-descriptions>
                    </div>
                </div>
            </template>
        </DetailModal>

        <!-- 核准 Modal -->
        <a-modal
            v-model:open="approveModalVisible"
            title="核准取消申請"
            :confirm-loading="approveLoading"
            :ok-text="isApproveManualRefund ? '確認核准（人工退款）' : '確認核准'"
            cancel-text="取消"
            :width="800"
            @ok="handleApproveConfirm"
            @cancel="handleApproveCancel"
        >
            <div class="approve-modal-content">
                <!-- 申請資訊 -->
                <div v-if="currentApproveRecord" class="request-info-section">
                    <h4 class="info-title">申請資訊</h4>
                    <div class="p-3 rounded-lg border text-xs font-bold mb-3 mt-2 flex items-center justify-between"
                        :class="approvePaymentBannerClass">
                        <span class="text-xs font-semibold">
                            {{ approvePaymentHint }}
                        </span>
                    </div>
                    <a-descriptions :column="2" bordered size="small">
                        <a-descriptions-item label="申請 ID">
                            {{ currentApproveRecord.id }}
                        </a-descriptions-item>
                        <a-descriptions-item label="訂單編號">
                            {{ currentApproveRecord.order_no }}
                        </a-descriptions-item>
                        <a-descriptions-item label="會員名稱">
                            {{ currentApproveRecord.user_name || '-' }}
                        </a-descriptions-item>
                        <a-descriptions-item label="訂單金額">
                            {{ formatAmount(currentApproveRecord.order_total) }}
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="
                                currentApproveRecord.point_discounts &&
                                currentApproveRecord.point_discounts.length > 0
                            "
                            label="使用點數"
                        >
                            <div class="points-display">
                                <span
                                    v-for="(
                                        discount, idx
                                    ) in currentApproveRecord.point_discounts"
                                    :key="discount.id || idx"
                                    class="point-display-item"
                                >
                                    {{ discount.point_type_code }}
                                    {{ formatAmount(discount.amount) }}
                                </span>
                            </div>
                        </a-descriptions-item>
                        <a-descriptions-item label="取消原因" :span="2">
                            {{ currentApproveRecord.reason || '-' }}
                        </a-descriptions-item>
                        <a-descriptions-item label="申請退款品項預估總額">
                            <span class="text-amber-700 font-bold">
                                NT$ {{ (currentApproveRecord.requested_amount || currentApproveRecord.order_total || 0).toLocaleString() }}
                            </span>
                        </a-descriptions-item>
                        <a-descriptions-item label="申請時間">
                            {{
                                formatDateTime(currentApproveRecord.created_at)
                            }}
                        </a-descriptions-item>
                    </a-descriptions>
                </div>

                <!-- 學員選擇之退款商品明細 -->
                <div v-if="currentApproveRecord?.items_payload && currentApproveRecord.items_payload.length > 0" class="mt-4 mb-4">
                    <h4 class="info-title">學員申請退款商品與數量明細</h4>
                    <a-table
                        :data-source="getFormattedRequestItems(currentApproveRecord)"
                        :columns="requestItemColumns"
                        :pagination="false"
                        size="small"
                        bordered
                        row-key="order_item_id"
                    />
                </div>

                <!-- 審核與扣除手續費區塊 -->
                <a-form
                    :model="approveForm"
                    layout="vertical"
                    class="approve-form mt-4"
                >
                    <a-row :gutter="16">
                        <a-col :span="12">
                            <a-form-item label="行政手續費 (NT$)">
                                <a-input-number
                                    v-model:value="approveForm.fee_amount"
                                    :min="0"
                                    :max="currentApproveRecord?.requested_amount || currentApproveRecord?.order_total || 0"
                                    :disabled="approvePaymentStatus?.can_cancel_auth"
                                    class="w-full"
                                    placeholder="尚未請款時不可設手續費"
                                />
                            </a-form-item>
                        </a-col>
                        <a-col :span="12">
                            <a-form-item label="手續費說明">
                                <a-input
                                    v-model:value="approveForm.fee_note"
                                    :disabled="approvePaymentStatus?.can_cancel_auth"
                                    placeholder="例如：行政處理手續費 10%"
                                />
                            </a-form-item>
                        </a-col>
                    </a-row>

                    <div class="refund-summary-box">
                        <div class="flex items-center justify-between">
                            <span class="refund-label">{{ getActualRefundLabel(currentApproveRecord) }}：</span>
                            <span class="refund-value">
                                NT$ {{ computedActualRefund.toLocaleString() }}
                            </span>
                        </div>
                        <div v-if="approveForm.fee_amount > 0" class="text-xs text-amber-800 font-medium mt-1">
                            (依申請預估總額 NT$ {{ (currentApproveRecord?.requested_amount || 0).toLocaleString() }} 扣除行政手續費 NT$ {{ approveForm.fee_amount.toLocaleString() }})
                        </div>
                    </div>

                    <a-form-item label="審核備註（選填）">
                        <a-textarea
                            v-model:value="approveForm.note"
                            :rows="3"
                            placeholder="請輸入審核備註"
                            :maxlength="500"
                            show-count
                        />
                    </a-form-item>
                </a-form>
            </div>
        </a-modal>

        <!-- 拒絕 Modal -->
        <a-modal
            v-model:open="rejectModalVisible"
            title="拒絕取消申請"
            :confirm-loading="rejectLoading"
            ok-text="確認拒絕"
            cancel-text="取消"
            :width="800"
            @ok="handleRejectConfirm"
            @cancel="handleRejectCancel"
        >
            <div class="reject-modal-content">
                <!-- 申請資訊 -->
                <div v-if="currentRejectRecord" class="request-info-section">
                    <h4 class="info-title">申請資訊</h4>
                    <a-descriptions :column="2" bordered size="small">
                        <a-descriptions-item label="申請 ID">
                            {{ currentRejectRecord.id }}
                        </a-descriptions-item>
                        <a-descriptions-item label="訂單編號">
                            {{ currentRejectRecord.order_no }}
                        </a-descriptions-item>
                        <a-descriptions-item label="會員名稱">
                            {{ currentRejectRecord.user_name || '-' }}
                        </a-descriptions-item>
                        <a-descriptions-item label="訂單金額">
                            {{ formatAmount(currentRejectRecord.order_total) }}
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="
                                currentRejectRecord.point_discounts &&
                                currentRejectRecord.point_discounts.length > 0
                            "
                            label="使用點數"
                        >
                            <div class="points-display">
                                <span
                                    v-for="(
                                        discount, idx
                                    ) in currentRejectRecord.point_discounts"
                                    :key="discount.id || idx"
                                    class="point-display-item"
                                >
                                    {{ discount.point_type_code }}
                                    {{ formatAmount(discount.amount) }}
                                </span>
                            </div>
                        </a-descriptions-item>
                        <a-descriptions-item label="取消原因" :span="2">
                            {{ currentRejectRecord.reason || '-' }}
                        </a-descriptions-item>
                        <a-descriptions-item label="申請時間" :span="2">
                            {{ formatDateTime(currentRejectRecord.created_at) }}
                        </a-descriptions-item>
                    </a-descriptions>
                </div>

                <div
                    v-if="
                        currentRejectRecord?.items_payload &&
                        currentRejectRecord.items_payload.length > 0
                    "
                    class="mt-4 mb-2"
                >
                    <h4 class="info-title">學員申請退款商品與數量明細</h4>
                    <a-table
                        :data-source="
                            getFormattedRequestItems(currentRejectRecord)
                        "
                        :columns="requestItemColumns"
                        :pagination="false"
                        size="small"
                        bordered
                        row-key="order_item_id"
                    />
                </div>

                <!-- 拒絕原因輸入 -->
                <a-form
                    :model="rejectForm"
                    layout="vertical"
                    class="reject-form"
                >
                    <a-form-item
                        label="拒絕原因"
                        :rules="[
                            {
                                required: true,
                                message: '請輸入拒絕原因'
                            }
                        ]"
                    >
                        <a-textarea
                            v-model:value="rejectForm.note"
                            :rows="4"
                            placeholder="請輸入拒絕原因（必填，最多 500 字）"
                            :maxlength="500"
                            show-count
                        />
                    </a-form-item>
                </a-form>
            </div>
        </a-modal>

        <!-- 批量拒絕 Modal -->
        <a-modal
            v-model:open="batchRejectModalVisible"
            title="批量拒絕取消申請"
            :confirm-loading="batchLoading"
            ok-text="確認拒絕"
            cancel-text="取消"
            ok-type="danger"
            @ok="handleBatchRejectConfirm"
            @cancel="handleBatchRejectCancel"
        >
            <div class="reject-modal-content">
                <p class="batch-reject-hint">
                    將拒絕已選取的
                    <strong>{{ selectedRowKeys.length }}</strong>
                    筆待審核申請，請填寫統一的拒絕原因。
                </p>
                <a-form layout="vertical">
                    <a-form-item
                        label="拒絕原因"
                        required
                        :rules="[
                            {
                                required: true,
                                message: '請輸入拒絕原因'
                            }
                        ]"
                    >
                        <a-textarea
                            v-model:value="batchRejectNote"
                            :rows="4"
                            placeholder="請輸入拒絕原因（必填，最多 500 字）"
                            :maxlength="500"
                            show-count
                        />
                    </a-form-item>
                </a-form>
            </div>
        </a-modal>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { message, Modal } from 'ant-design-vue';
import { showErrorMessage } from '@/utils/messageHelper';
import { useRouter } from 'vue-router';
import {
    getCancellationRequests,
    getCancellationRequest,
    approveCancellationRequest,
    rejectCancellationRequest,
    retryRefund
} from '@/api/products/orderCancellations';
import DataTable from '@/components/common/DataTable.vue';
import DetailModal from '@/components/common/DetailModal.vue';
import formatDateTime from '@/utils/formatDateTime';
import dayjs from 'dayjs';
import { useAdminInfo } from '@/stores/useAdminInfo';
import { isNewebPayCode } from '@/utils/paymentUtils';

const router = useRouter();
const adminStore = useAdminInfo();

// 權限檢查
const canViewRequest = computed(() => adminStore.hasPermissionCode('150501')); // 查看申請
const canApproveRequest = computed(() =>
    adminStore.hasPermissionCode('150502')
); // 核准申請
const canRejectRequest = computed(() => adminStore.hasPermissionCode('150503')); // 拒絕申請
const canRetryRefund = computed(() => adminStore.hasPermissionCode('150504')); // 重試退款
const canViewMember = computed(() => adminStore.hasPermissionCode('100110')); // 查看會員詳情

// 資料狀態
const requests = ref([]);
const loading = ref(false);
const selectedRowKeys = ref([]);
const batchLoading = ref(false);
const batchRejectModalVisible = ref(false);
const batchRejectNote = ref('');

const rowSelection = computed(() => ({
    selectedRowKeys: selectedRowKeys.value,
    onChange: (keys) => {
        selectedRowKeys.value = keys;
    },
    getCheckboxProps: (record) => ({
        disabled: record.status !== 'pending'
    })
}));

// 篩選器狀態
const filters = reactive({
    status: undefined, // 預設顯示全部
    refund_status: undefined,
    order_no: '',
    from: undefined,
    to: undefined
});

// 日期範圍
const dateRange = ref(null);

// 分頁設定
const pagination = reactive({
    current: 1,
    pageSize: 15,
    total: 0
});

// Modal 狀態
const detailModalVisible = ref(false);
const selectedRequest = ref(null);

const requestItemColumns = [
    { title: '商品名稱', dataIndex: 'name', key: 'name' },
    {
        title: '單價',
        dataIndex: 'unit_price',
        key: 'unit_price',
        customRender: ({ value }) => `NT$ ${Number(value || 0).toLocaleString()}`
    },
    { title: '學員申請退款數量', dataIndex: 'qty', key: 'qty' },
    {
        title: '小計',
        dataIndex: 'subtotal',
        key: 'subtotal',
        customRender: ({ value }) => `NT$ ${Number(value || 0).toLocaleString()}`
    }
];

const getFormattedRequestItems = (record) => {
    if (!record || !record.items_payload || !Array.isArray(record.items_payload)) return [];
    const orderItems = record.order_items || record.order?.items || [];
    return record.items_payload.map(item => {
        const matched = orderItems.find(o => Number(o.id) === Number(item.order_item_id));
        return {
            order_item_id: item.order_item_id,
            name: matched?.name || matched?.name_snapshot || '商品',
            unit_price: item.unit_price || matched?.unit_price || matched?.unit_price_snapshot || 0,
            qty: item.qty || 1,
            subtotal: item.subtotal || (item.unit_price * item.qty) || 0
        };
    });
};

const isPartialCancellationRequest = (record) => {
    const payload = record?.items_payload;
    const orderItems = record?.order_items || record?.order?.items || [];
    if (!payload?.length) {
        return false;
    }
    if (payload.length < orderItems.length) {
        return true;
    }
    return payload.some((item) => {
        const orderItem = orderItems.find(
            (o) => Number(o.id) === Number(item.order_item_id)
        );
        if (!orderItem) {
            return false;
        }
        const maxQty = Math.max(
            0,
            Number(orderItem.qty || 0) - Number(orderItem.refunded_qty || 0)
        );
        return Number(item.qty || 0) < maxQty;
    });
};

const getCancellationScopeLabel = (record) =>
    isPartialCancellationRequest(record) ? '部分退款' : '整單取消';

const getCancellationItemsSummary = (record) => {
    const items = getFormattedRequestItems(record);
    if (!items.length) {
        return '全部商品';
    }
    const head = items
        .slice(0, 2)
        .map((item) => `${item.name} ×${item.qty}`)
        .join('、');
    if (items.length > 2) {
        return `${head} 等${items.length}項`;
    }
    return head;
};

// 核准 Modal
const approveModalVisible = ref(false);
const approveLoading = ref(false);
const approvePaymentStatus = ref(null);
const approveForm = reactive({
    note: '',
    fee_amount: 0,
    fee_note: ''
});
const currentApproveId = ref(null);
const currentApproveRecord = ref(null);

/** ATM／WebATM／銀行轉帳：不走藍新線上退刷，核准後改人工匯款 */
const isManualRefundPayment = (record, paymentStatus = null) => {
    const gateway = (
        record?.payment_gateway ||
        record?.payment?.gateway ||
        record?.order?.payment_gateway ||
        paymentStatus?.gateway ||
        ''
    ).toLowerCase();
    const method = (
        record?.payment_method ||
        paymentStatus?.payment_type ||
        paymentStatus?.payment_method ||
        ''
    ).toLowerCase();

    return (
        gateway === 'bank_transfer' ||
        ['atm', 'vacc', 'webatm'].includes(method)
    );
};

const isApproveManualRefund = computed(() =>
    isManualRefundPayment(currentApproveRecord.value, approvePaymentStatus.value)
);

/** 未付款／查無交易：核准即取消，無需退款 */
const isApproveUnpaid = computed(() => {
    const ps = approvePaymentStatus.value;
    const record = currentApproveRecord.value;
    if (record?.refund_required === false || record?.refund_status === 'none') {
        return true;
    }
    const status = (ps?.newebpay_status || ps?.status || '').toLowerCase();
    if (status === 'unpaid') return true;
    const msg = ps?.message || ps?.newebpay_status_label || '';
    return typeof msg === 'string' && msg.includes('查無交易資料');
});

const computedActualRefund = computed(() => {
    if (isApproveUnpaid.value) {
        return 0;
    }
    const requested = currentApproveRecord.value?.requested_amount || currentApproveRecord.value?.order_total || 0;
    const fee = approveForm.fee_amount || 0;
    return Math.max(0, requested - fee);
});

/** 與 NewebPayGateway::shortHint / 訂單退款 Modal 對齊 */
const approvePaymentHint = computed(() => {
    if (isApproveUnpaid.value) {
        return '此訂單尚未完成付款，核准後將直接取消訂單（無需退款）。';
    }
    if (isApproveManualRefund.value) {
        return 'ATM／網路銀行轉帳無法透過藍新線上退款；核准後請人工匯款給會員（藍新仍會顯示已付款）。';
    }
    const ps = approvePaymentStatus.value;
    if (!ps) return '核准後將依金流狀態自動取消授權或退款。';
    if (ps.can_cancel_auth) {
        return '尚未請款，僅能全額取消（取消授權），無法只退部分商品。';
    }
    if (ps.can_refund) {
        return '已請款，可執行退款。';
    }
    const status = ps.newebpay_status;
    if (status === 'cancelled') {
        return '此交易已取消授權，無法再申請退款。';
    }
    if (status === 'refunded') {
        return '此交易已退款，無法重複申請。';
    }
    return '目前無法取消授權或退款。';
});

const approvePaymentBannerClass = computed(() => {
    if (isApproveUnpaid.value) {
        return 'bg-emerald-50 border-emerald-200 text-emerald-800';
    }
    if (isApproveManualRefund.value) {
        return 'bg-sky-50 border-sky-200 text-sky-800';
    }
    const ps = approvePaymentStatus.value;
    if (ps?.newebpay_status === 'cancelled' || ps?.newebpay_status === 'refunded') {
        return 'bg-red-50 border-red-200 text-red-700';
    }
    if (ps?.can_refund) {
        return 'bg-emerald-50 border-emerald-200 text-emerald-700';
    }
    return 'bg-amber-50 border-amber-200 text-amber-800';
});

// 拒絕 Modal
const rejectModalVisible = ref(false);
const rejectLoading = ref(false);
const rejectForm = reactive({
    note: ''
});
const currentRejectId = ref(null);
const currentRejectRecord = ref(null);

// 申請狀態選項
const statusOptions = [
    { label: '全部', value: undefined },
    { label: '待審核', value: 'pending' },
    { label: '已核准', value: 'approved' },
    { label: '已拒絕', value: 'rejected' }
];

// 退款狀態選項
const refundStatusOptions = [
    { label: '全部', value: undefined },
    { label: '無需退款', value: 'none' },
    { label: '待退款', value: 'pending' },
    { label: '退款處理中', value: 'processing' },
    { label: '退款完成', value: 'completed' },
    { label: '退款失敗', value: 'failed' }
];

// 表格欄位定義
const columns = [
    {
        title: '訂單編號',
        key: 'order_no',
        width: 148,
        fixed: 'left'
    },
    {
        title: '會員',
        key: 'user_name',
        width: 96
    },
    {
        title: '退款範圍',
        key: 'refund_scope',
        width: 168,
        ellipsis: true
    },
    {
        title: '申請退款',
        key: 'requested_amount',
        width: 100,
        align: 'right'
    },
    {
        title: '訂單金額',
        key: 'order_total',
        width: 96,
        align: 'right'
    },
    {
        title: '使用點數',
        key: 'points',
        width: 120,
        align: 'right'
    },
    {
        title: '申請狀態',
        key: 'status',
        width: 96,
        align: 'center'
    },
    {
        title: '退款狀態',
        key: 'refund_status',
        width: 108,
        align: 'center'
    },
    {
        title: '申請時間',
        key: 'created_at',
        width: 138
    },
    {
        title: '審核時間',
        key: 'reviewed_at',
        width: 138
    },
    {
        title: '操作',
        key: 'actions',
        width: 168,
        fixed: 'right',
        align: 'center'
    }
];

// 載入取消申請列表
const loadRequests = async () => {
    try {
        loading.value = true;

        const params = {
            page: pagination.current,
            per_page: pagination.pageSize
        };

        // 添加篩選條件
        if (filters.status) params.status = filters.status;
        if (filters.refund_status) params.refund_status = filters.refund_status;
        if (filters.order_no) params.order_no = filters.order_no;
        if (filters.from) params.from = filters.from;
        if (filters.to) params.to = filters.to;

        const response = await getCancellationRequests(params);

        if (response && response.result) {
            requests.value = response.data?.items || [];
            pagination.total = response.data?.total || 0;
            pagination.current = response.data?.current_page || 1;
            // 列表刷新後清掉已不在頁面的勾選
            const idSet = new Set(requests.value.map((r) => r.id));
            selectedRowKeys.value = selectedRowKeys.value.filter((id) =>
                idSet.has(id)
            );
        } else {
            console.warn('取消申請列表回應異常:', response);
            requests.value = [];
            pagination.total = 0;
        }
    } catch (error) {
        console.error('載入取消申請列表失敗:', error);
        showErrorMessage(
            '載入取消申請列表失敗: ' + (error.message || '未知錯誤')
        );
        requests.value = [];
        pagination.total = 0;
    } finally {
        loading.value = false;
    }
};

// 日期範圍變更
const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
        filters.from = dayjs(dates[0]).format('YYYY-MM-DD');
        filters.to = dayjs(dates[1]).format('YYYY-MM-DD');
    } else {
        filters.from = undefined;
        filters.to = undefined;
        dateRange.value = null;
    }
};

// 查詢
const handleSearch = () => {
    pagination.current = 1;
    loadRequests();
};

// 重置
const handleReset = () => {
    filters.status = undefined; // 重置時恢復為全部
    filters.refund_status = undefined;
    filters.order_no = '';
    filters.from = undefined;
    filters.to = undefined;
    dateRange.value = null;
    pagination.current = 1;
    loadRequests();
};

// 表格變化處理
const handleTableChange = ({ pagination: pag }) => {
    if (pag) {
        pagination.current = pag.current;
        pagination.pageSize = pag.pageSize;
        loadRequests();
    }
};

// 狀態相關函數
const getStatusText = (status) => {
    const statusMap = {
        pending: '待審核',
        approved: '已核准',
        rejected: '已拒絕'
    };
    return statusMap[status] || status || '-';
};

const getStatusColor = (status) => {
    const colorMap = {
        pending: 'orange',
        approved: 'green',
        rejected: 'red'
    };
    return colorMap[status] || 'default';
};

const getRefundStatusText = (status) => {
    const statusMap = {
        none: '無需退款',
        pending: '待退款',
        processing: '退款處理中',
        completed: '退款完成',
        failed: '退款失敗'
    };
    return statusMap[status] || status || '-';
};

const getRefundStatusColor = (status) => {
    const colorMap = {
        none: 'default',
        pending: 'orange',
        processing: 'blue',
        completed: 'green',
        failed: 'red'
    };
    return colorMap[status] || 'default';
};

// 金額格式化
const formatAmount = (amount) => {
    if (!amount && amount !== 0) return '0';
    return Number(amount).toLocaleString('zh-TW');
};

// 點數標籤顏色
const getPointTagColor = (pointTypeCode) => {
    const colorMap = {
        'POINT': 'blue',
        'BONUS': 'green',
        'REWARD': 'purple',
        'CREDIT': 'orange',
        'COUPON': 'cyan'
    };
    return colorMap[pointTypeCode] || 'default';
};

// 依據支付方式動態取得退款標籤名稱
const getActualRefundLabel = (record) => {
    if (!record) return '實際退款金額';
    if (
        record.refund_required === false ||
        record.refund_status === 'none' ||
        (record === currentApproveRecord.value && isApproveUnpaid.value)
    ) {
        return '取消後無需退款';
    }
    if (isManualRefundPayment(record, approvePaymentStatus.value)) {
        return '預估人工退款金額';
    }
    const gateway = record.payment_gateway || record.payment_method || record.payment?.gateway || record.order?.payment_gateway || '';
    if (isNewebPayCode(gateway)) {
        return '藍新實際退款金額';
    } else if (gateway === 'bank_transfer') {
        return '銀行轉帳實際退款金額';
    } else if (gateway === 'point_exchange') {
        return '實際退還權益金額';
    }
    return '實際退款金額';
};

// 申請基本資訊（用於 DetailModal）
const requestBasicInfo = computed(() => {
    if (!selectedRequest.value) return [];
    const request = selectedRequest.value;
    const info = [
        { label: '申請 ID', value: request.id },
        { label: '訂單編號', value: request.order_no },
        {
            label: '退款範圍',
            value: getCancellationScopeLabel(request)
        },
        {
            label: '申請退款金額',
            value: formatAmount(request.requested_amount)
        },
        { label: '訂單金額', value: formatAmount(request.order_total) },
        { label: '會員名稱', value: request.user_name || '-' }
    ];

    // 如果有使用點數，顯示點數資訊（放在金額附近）
    if (request.point_discounts && request.point_discounts.length > 0) {
        const pointsText = request.point_discounts
            .map(
                (discount) =>
                    `${discount.point_type_code} ${formatAmount(discount.amount)}`
            )
            .join(', ');
        info.splice(2, 0, { label: '使用點數', value: pointsText });
    }

    info.push(
        {
            label: '申請狀態',
            value: request.status_label || getStatusText(request.status)
        },
        {
            label: '退款狀態',
            value: request.refund_required
                ? request.refund_status_label ||
                  getRefundStatusText(request.refund_status)
                : '無需退款'
        },
        { label: '申請時間', value: formatDateTime(request.created_at) },
        {
            label: '審核時間',
            value: request.reviewed_at
                ? formatDateTime(request.reviewed_at)
                : '-'
        }
    );

    return info;
});

// 查看申請詳情
const handleViewDetail = async (record) => {
    if (!canViewRequest.value) {
        message.warning('您沒有查看申請詳情的權限');
        return;
    }
    try {
        loading.value = true;
        const response = await getCancellationRequest(record.id);
        if (response && response.result) {
            selectedRequest.value = response.data;
            detailModalVisible.value = true;
        } else {
            showErrorMessage('載入申請詳情失敗');
        }
    } catch (error) {
        console.error('載入申請詳情失敗:', error);
        showErrorMessage('載入申請詳情失敗: ' + (error.message || '未知錯誤'));
    } finally {
        loading.value = false;
    }
};

// 查看會員詳情
const handleViewMember = (userId) => {
    if (!userId) return;
    if (!canViewMember.value) {
        message.warning('您沒有查看會員詳情的權限');
        return;
    }
    router.push(`/members/${userId}`);
};

// 核准申請
const handleApprove = async (record) => {
    if (!canApproveRequest.value) {
        message.warning('您沒有核准申請的權限');
        return;
    }
    currentApproveId.value = record.id;
    currentApproveRecord.value = record;
    approveForm.note = '';
    approveForm.fee_amount = 0;
    approveForm.fee_note = '';
    approvePaymentStatus.value = null;
    approveModalVisible.value = true;

    const orderId = record.order_id || record.order?.id;
    if (orderId) {
        try {
            const { getPaymentStatus } = await import('@/api/products/orders');
            const res = await getPaymentStatus(orderId);
            if (res && (res.result || res.success) && res.data) {
                approvePaymentStatus.value = res.data;
                if (res.data.can_cancel_auth) {
                    approveForm.fee_amount = 0;
                    approveForm.fee_note = '';
                }
            } else if (
                typeof res?.message === 'string' &&
                res.message.includes('查無交易資料')
            ) {
                approvePaymentStatus.value = {
                    status: 'unpaid',
                    newebpay_status: 'unpaid',
                    newebpay_status_label: '未付款',
                    can_cancel_auth: false,
                    can_refund: false,
                    message: res.message
                };
            }
        } catch (err) {
            const msg = err?.message || err?.response?.data?.message || '';
            if (typeof msg === 'string' && msg.includes('查無交易資料')) {
                approvePaymentStatus.value = {
                    status: 'unpaid',
                    newebpay_status: 'unpaid',
                    newebpay_status_label: '未付款',
                    can_cancel_auth: false,
                    can_refund: false,
                    message: msg
                };
            } else {
                console.error('查詢金流實時狀態失敗:', err);
            }
        }
    }
};

const handleApproveConfirm = async () => {
    try {
        approveLoading.value = true;
        const response = await approveCancellationRequest(
            currentApproveId.value,
            {
                note: approveForm.note || undefined,
                fee_amount: approveForm.fee_amount,
                fee_note: approveForm.fee_note || undefined
            }
        );
        if (response && response.result) {
            const msg =
                response.message && response.message !== 'success'
                    ? response.message
                    : '已核准取消申請';
            message.success(msg);
            approveModalVisible.value = false;
            loadRequests();
        } else {
            showErrorMessage(response?.message || '核准失敗');
        }
    } catch (error) {
        console.error('核准失敗:', error);
        showErrorMessage('核准失敗: ' + (error.message || '未知錯誤'));
    } finally {
        approveLoading.value = false;
    }
};

const handleApproveCancel = () => {
    approveModalVisible.value = false;
    approveForm.note = '';
    approveForm.fee_amount = 0;
    approveForm.fee_note = '';
    currentApproveId.value = null;
    currentApproveRecord.value = null;
};

// 拒絕申請
const handleReject = (record) => {
    if (!canRejectRequest.value) {
        message.warning('您沒有拒絕申請的權限');
        return;
    }
    currentRejectId.value = record.id;
    currentRejectRecord.value = record;
    rejectForm.note = '';
    rejectModalVisible.value = true;
};

const handleRejectConfirm = async () => {
    if (!rejectForm.note || !rejectForm.note.trim()) {
        showErrorMessage('請輸入拒絕原因');
        return;
    }

    if (rejectForm.note.length > 500) {
        showErrorMessage('拒絕原因最多 500 字');
        return;
    }

    try {
        rejectLoading.value = true;
        const response = await rejectCancellationRequest(
            currentRejectId.value,
            { note: rejectForm.note.trim() }
        );
        if (response && response.result) {
            const msg =
                response.message && response.message !== 'success'
                    ? response.message
                    : '已拒絕取消申請';
            message.success(msg);
            rejectModalVisible.value = false;
            loadRequests();
        } else {
            showErrorMessage(response?.message || '拒絕失敗');
        }
    } catch (error) {
        console.error('拒絕失敗:', error);
        showErrorMessage('拒絕失敗: ' + (error.message || '未知錯誤'));
    } finally {
        rejectLoading.value = false;
    }
};

const handleRejectCancel = () => {
    rejectModalVisible.value = false;
    rejectForm.note = '';
    currentRejectId.value = null;
    currentRejectRecord.value = null;
};

const handleBatchApprove = () => {
    if (!canApproveRequest.value) {
        message.warning('您沒有核准申請的權限');
        return;
    }
    const ids = [...selectedRowKeys.value];
    if (ids.length === 0) {
        message.warning('請先勾選待審核的申請');
        return;
    }

    Modal.confirm({
        title: '確認批量核准',
        content: `即將核准 ${ids.length} 筆待審核申請（手續費預設 0）。需調整手續費請改用單筆核准。`,
        okText: '確認核准',
        cancelText: '取消',
        onOk: async () => {
            batchLoading.value = true;
            let successCount = 0;
            const errors = [];
            try {
                for (const id of ids) {
                    try {
                        const response = await approveCancellationRequest(id, {
                            fee_amount: 0
                        });
                        if (response && response.result) {
                            successCount += 1;
                        } else {
                            errors.push(
                                `#${id}: ${response?.message || '核准失敗'}`
                            );
                        }
                    } catch (error) {
                        errors.push(
                            `#${id}: ${error?.message || '核准失敗'}`
                        );
                    }
                }
                if (successCount > 0) {
                    message.success(`已核准 ${successCount} 筆申請`);
                }
                if (errors.length > 0) {
                    showErrorMessage(
                        `部分失敗（${errors.length}）：${errors.slice(0, 3).join('；')}`
                    );
                }
                selectedRowKeys.value = [];
                await loadRequests();
            } finally {
                batchLoading.value = false;
            }
        }
    });
};

const handleBatchRejectOpen = () => {
    if (!canRejectRequest.value) {
        message.warning('您沒有拒絕申請的權限');
        return;
    }
    if (selectedRowKeys.value.length === 0) {
        message.warning('請先勾選待審核的申請');
        return;
    }
    batchRejectNote.value = '';
    batchRejectModalVisible.value = true;
};

const handleBatchRejectCancel = () => {
    batchRejectModalVisible.value = false;
    batchRejectNote.value = '';
};

const handleBatchRejectConfirm = async () => {
    const note = (batchRejectNote.value || '').trim();
    if (!note) {
        showErrorMessage('請輸入拒絕原因');
        return Promise.reject();
    }
    if (note.length > 500) {
        showErrorMessage('拒絕原因最多 500 字');
        return Promise.reject();
    }

    const ids = [...selectedRowKeys.value];
    batchLoading.value = true;
    let successCount = 0;
    const errors = [];
    try {
        for (const id of ids) {
            try {
                const response = await rejectCancellationRequest(id, { note });
                if (response && response.result) {
                    successCount += 1;
                } else {
                    errors.push(`#${id}: ${response?.message || '拒絕失敗'}`);
                }
            } catch (error) {
                errors.push(`#${id}: ${error?.message || '拒絕失敗'}`);
            }
        }
        if (successCount > 0) {
            message.success(`已拒絕 ${successCount} 筆申請`);
        }
        if (errors.length > 0) {
            showErrorMessage(
                `部分失敗（${errors.length}）：${errors.slice(0, 3).join('；')}`
            );
        }
        selectedRowKeys.value = [];
        batchRejectModalVisible.value = false;
        batchRejectNote.value = '';
        await loadRequests();
    } finally {
        batchLoading.value = false;
    }
};

// 重試退款
const handleRetryRefund = (record) => {
    Modal.confirm({
        title: '確認重試退款',
        content: `確定要為申請 ${record.id}（訂單 ${record.order_no}）重試退款嗎？`,
        okText: '確認',
        cancelText: '取消',
        onOk: async () => {
            try {
                loading.value = true;
                const response = await retryRefund(record.id);
                if (response && response.result) {
                    const msg =
                        response.message && response.message !== 'success'
                            ? response.message
                            : '退款重試成功';
                    message.success(msg);
                    loadRequests();
                } else {
                    showErrorMessage(response?.message || '退款重試失敗');
                }
            } catch (error) {
                console.error('退款重試失敗:', error);
                showErrorMessage(
                    '退款重試失敗: ' + (error.message || '未知錯誤')
                );
            } finally {
                loading.value = false;
            }
        }
    });
};

// 初始化
onMounted(() => {
    loadRequests();
});
</script>

<style scoped>
.order-cancellations-page {
    padding: 24px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    min-height: 100vh;
}

.batch-approve-btn {
    border-color: #52c41a;
    color: #389e0d;
}
.batch-approve-btn:hover {
    border-color: #73d13d !important;
    color: #52c41a !important;
}
.batch-reject-btn {
    border-color: #ff4d4f;
    color: #cf1322;
}
.batch-reject-btn:hover {
    border-color: #ff7875 !important;
    color: #ff4d4f !important;
}
.batch-reject-hint {
    margin: 0 0 12px;
    color: rgba(0, 0, 0, 0.65);
}

.page-header {
    margin-bottom: 24px;
}

.page-header h1 {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 8px 0;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.page-header p {
    margin: 0;
    color: #6a6a7a;
    font-size: 14px;
    font-family: 'Courier New', monospace;
}

.content {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 16px;
    padding: 32px;
    min-height: 500px;
    position: relative;
    box-shadow: 0 0 30px rgba(42, 74, 122, 0.3);
    transition: all 0.3s ease;
}

.content::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
    opacity: 0.8;
    z-index: 0;
}

/* ===== 搜尋與篩選區域 ===== */
.search-filter-section {
    margin-bottom: 32px;
    position: relative;
    z-index: 1;
}

.toolbar {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 8px;
    flex-wrap: wrap;
    gap: 16px;
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(200, 200, 210, 0.4);
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.1);
}

.search-box {
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex: 1;
}

.search-row {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: flex-end;
}

.w-60 {
    width: 240px;
    min-width: 200px;
}

.w-40 {
    width: 160px;
    min-width: 140px;
}

.toolbar-actions {
    flex-shrink: 0;
}

/* 訂單編號輸入框樣式 */
.order-no-input :deep(.ant-input) {
    background: rgba(255, 255, 255, 0.95) !important;
    border-radius: 8px !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    transition: all 0.3s ease !important;
}

.order-no-input :deep(.ant-input:hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.2) !important;
}

.order-no-input :deep(.ant-input:focus) {
    border-color: rgba(42, 74, 122, 0.8) !important;
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.2) !important;
}

.order-no-input :deep(.ant-input::placeholder) {
    color: rgba(42, 42, 58, 0.5) !important;
    font-family: 'Courier New', monospace !important;
}

/* 下拉選單樣式 */
.status-select :deep(.ant-select-selector) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 8px !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    height: 32px !important;
    transition: all 0.3s ease !important;
}

.status-select :deep(.ant-select-selector:hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.2) !important;
}

.status-select :deep(.ant-select-focused .ant-select-selector) {
    border-color: rgba(42, 74, 122, 0.8) !important;
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.2) !important;
}

.status-select :deep(.ant-select-selection-item),
.status-select :deep(.ant-select-selection-placeholder) {
    line-height: 30px !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
}

/* 日期選擇器樣式 */
.status-select :deep(.ant-picker) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 8px !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    height: 32px !important;
    transition: all 0.3s ease !important;
}

.status-select :deep(.ant-picker:hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.2) !important;
}

.status-select :deep(.ant-picker-focused) {
    border-color: rgba(42, 74, 122, 0.8) !important;
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.2) !important;
}

.order-no-link {
    color: #3b6ea5;
    font-family: 'Courier New', monospace;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.2s ease;
    font-size: 14px;
}

.order-no-link:hover {
    color: #9a8c98;
    text-decoration: underline;
}

.order-no-text {
    font-family: 'Courier New', monospace;
    font-weight: 600;
    color: #2a2a3a;
    font-size: 14px;
}

/* 會員連結 */
.user-link {
    color: #3b6ea5;
    font-family: 'Courier New', monospace;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.2s ease;
}

.user-link:hover {
    color: #9a8c98;
    text-decoration: underline;
}

.user-name {
    font-family: 'Courier New', monospace;
    color: #2a2a3a;
    font-weight: 500;
}

/* 點數顯示 */
.points-display {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    justify-content: flex-end;
    align-items: center;
}

.point-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-family: 'Courier New', monospace;
    font-size: 12px;
    padding: 2px 8px;
    border-radius: 4px;
    font-weight: 600;
}

.point-code {
    font-weight: 700;
    color: inherit;
}

.point-separator {
    color: rgba(42, 42, 58, 0.5);
    font-weight: 400;
}

.point-amount {
    font-weight: 600;
    color: inherit;
}

.no-points {
    color: #9a9aaa;
    font-family: 'Courier New', monospace;
}

.status-tag,
.refund-status-tag {
    font-weight: 500;
    font-family: 'Courier New', monospace;
}

.no-refund {
    color: #9a9aaa;
    font-family: 'Courier New', monospace;
}

.error-message {
    color: #ffa87a;
    font-weight: 500;
    font-family: 'Courier New', monospace;
}

.request-detail-content {
    position: relative;
    z-index: 1;
    font-family: 'Courier New', monospace;
}

.detail-section {
    margin-bottom: 32px;
}

.section-title {
    font-size: 18px;
    font-weight: 700;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 2px solid rgba(138, 175, 255, 0.3);
    text-shadow: 0 0 10px rgba(42, 74, 122, 0.3);
}

.request-detail-content :deep(.ant-descriptions) {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 12px;
    overflow: hidden;
    border: 2px solid rgba(138, 175, 255, 0.25);
    box-shadow: 0 4px 16px rgba(138, 175, 255, 0.15);
}

.request-detail-content :deep(.ant-descriptions-item-label) {
    background: linear-gradient(
        135deg,
        rgba(138, 175, 255, 0.12),
        rgba(42, 74, 122, 0.08)
    ) !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    border-right: 2px solid rgba(138, 175, 255, 0.2) !important;
    padding: 12px 16px !important;
    width: 140px;
}

.request-detail-content :deep(.ant-descriptions-item-content) {
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    padding: 12px 16px !important;
    background: rgba(255, 255, 255, 0.6) !important;
}

.request-detail-content :deep(.ant-descriptions-row) {
    border-bottom: 1px solid rgba(138, 175, 255, 0.15) !important;
}

.request-detail-content :deep(.ant-descriptions-row:last-child) {
    border-bottom: none !important;
}

.reason-text,
.notes-text {
    white-space: pre-wrap;
    word-wrap: break-word;
    line-height: 1.6;
    color: #2a2a3a;
}

.error-message {
    color: #ffa87a;
    font-weight: 700;
    font-family: 'Courier New', monospace;
    background: rgba(255, 168, 122, 0.1);
    padding: 8px 12px;
    border-radius: 6px;
    display: inline-block;
    white-space: pre-wrap;
    word-wrap: break-word;
    line-height: 1.6;
}

/* 基本資訊區塊樣式 */
:deep(.info-section .ant-descriptions) {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 12px;
    overflow: hidden;
    border: 2px solid rgba(138, 175, 255, 0.25);
    box-shadow: 0 4px 16px rgba(138, 175, 255, 0.15);
}

:deep(.info-section .ant-descriptions-item-label) {
    background: linear-gradient(
        135deg,
        rgba(138, 175, 255, 0.12),
        rgba(42, 74, 122, 0.08)
    ) !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    border-right: 2px solid rgba(138, 175, 255, 0.2) !important;
    padding: 12px 16px !important;
}

:deep(.info-section .ant-descriptions-item-content) {
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    padding: 12px 16px !important;
    background: rgba(255, 255, 255, 0.6) !important;
}

:deep(.info-section .ant-descriptions-row) {
    border-bottom: 1px solid rgba(138, 175, 255, 0.15) !important;
}

:deep(.info-section .ant-descriptions-row:last-child) {
    border-bottom: none !important;
}

/* 拒絕 Modal 樣式 */
.reject-modal-content {
    font-family: 'Courier New', monospace;
}

.request-info-section {
    margin-bottom: 24px;
}

.info-title {
    font-size: 16px;
    font-weight: 700;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 2px solid rgba(138, 175, 255, 0.3);
}

.request-info-section :deep(.ant-descriptions) {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 12px;
    overflow: hidden;
    border: 2px solid rgba(138, 175, 255, 0.25);
    box-shadow: 0 4px 16px rgba(138, 175, 255, 0.15);
}

.request-info-section :deep(.ant-descriptions-item-label) {
    background: linear-gradient(
        135deg,
        rgba(138, 175, 255, 0.12),
        rgba(42, 74, 122, 0.08)
    ) !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    border-right: 2px solid rgba(138, 175, 255, 0.2) !important;
    padding: 10px 14px !important;
}

.request-info-section :deep(.ant-descriptions-item-content) {
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    padding: 10px 14px !important;
    background: rgba(255, 255, 255, 0.6) !important;
}

.request-info-section :deep(.ant-descriptions-row) {
    border-bottom: 1px solid rgba(138, 175, 255, 0.15) !important;
}

.request-info-section :deep(.ant-descriptions-row:last-child) {
    border-bottom: none !important;
}

/* 核准 Modal 樣式 */
.approve-modal-content {
    font-family: 'Courier New', monospace;
}

.approve-form {
    margin-top: 16px;
}

.approve-form :deep(.ant-form-item-label > label) {
    font-family: 'Courier New', monospace;
    font-weight: 600;
    color: #2a2a3a;
}

.approve-form :deep(.ant-input) {
    font-family: 'Courier New', monospace;
    border-radius: 8px;
    border: 2px solid rgba(138, 175, 255, 0.3);
}

.approve-form :deep(.ant-input:focus),
.approve-form :deep(.ant-input-focused) {
    border-color: #3b6ea5;
    box-shadow: 0 0 8px rgba(138, 175, 255, 0.3);
}

.reject-form {
    margin-top: 16px;
}

.reject-form :deep(.ant-form-item-label > label) {
    font-family: 'Courier New', monospace;
    font-weight: 600;
    color: #2a2a3a;
}

.reject-form :deep(.ant-input) {
    font-family: 'Courier New', monospace;
    border-radius: 8px;
    border: 2px solid rgba(138, 175, 255, 0.3);
}

.reject-form :deep(.ant-input:focus),
.reject-form :deep(.ant-input-focused) {
    border-color: #3b6ea5;
    box-shadow: 0 0 8px rgba(138, 175, 255, 0.3);
}

/* 點數顯示 */
.points-display {
    font-family: 'Courier New', monospace;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.point-display-item {
    font-size: 13px;
    color: #2a2a3a;
    font-weight: 500;
}

/* 表格樣式 */
:deep(.ant-table-thead > tr > th) {
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
}

:deep(.ant-table) {
    border: 1px solid rgba(200, 200, 210, 0.4) !important;
}

:deep(.ant-table-tbody > tr:hover > td) {
    background: rgba(42, 74, 122, 0.1) !important;
}

/* 藍新實際退刷金額卡片樣式 */
.refund-summary-box {
    background: #ecfeff;
    border: 2px solid #0891b2;
    border-radius: 10px;
    padding: 16px 20px;
    margin-top: 16px;
    margin-bottom: 16px;
    box-shadow: 0 4px 12px rgba(8, 145, 178, 0.15);
}

.refund-label {
    color: #0f172a;
    font-weight: 700;
    font-size: 16px;
    letter-spacing: 0.5px;
}

.refund-value {
    color: #0e7490;
    font-weight: 900;
    font-size: 26px;
}

.refund-scope-cell {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
    line-height: 1.35;
}

.refund-scope-tag {
    align-self: flex-start;
    margin: 0;
}

.refund-scope-summary {
    font-size: 11px;
    color: #4b5563;
    white-space: normal;
    word-break: break-word;
}

.requested-amount-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    line-height: 1.3;
}

.requested-amount-value {
    font-family: 'Courier New', monospace;
    font-weight: 700;
    color: #b45309;
}

.requested-amount-order-total {
    font-size: 10px;
    color: #9ca3af;
}

/* 響應式設計 */
@media (max-width: 768px) {
    .search-row {
        flex-direction: column;
    }

    .w-60,
    .w-40 {
        width: 100%;
        min-width: 100%;
    }

    .toolbar-actions {
        width: 100%;
        justify-content: flex-start;
    }
}
</style>
